import { defineStore } from 'pinia';
import { type Property, getDefaultProperty, PropertyType, PropertyVisibility } from '../models/property';
import { SYSTEM_OWNER_UID } from '~/utils/systemAccount';
import { type  FeatureOption } from '~/models/feature';
import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where, DocumentReference, DocumentSnapshot, type DocumentData, QuerySnapshot, QueryDocumentSnapshot } from 'firebase/firestore';
import { type  Effect } from '~/models/effect';
import { generatePropertyId } from '~/utils/idGenerator';

export interface PropertyStateInterface {
	properties: Property[];
	selectedProperty?: Property;
	failedFetches: Set<string>; // Track failed property fetches to prevent infinite loops
}

export const usePropertyStore = defineStore('PropertyStore', {
	state: (): PropertyStateInterface => ({
		properties: [],
		selectedProperty: undefined,
		failedFetches: new Set(),
	}),

	getters: {
		propertyById: (state) => {
			return (propertyId: string): Property | undefined =>
				state.properties.find((property) => property.id === propertyId);
		},
		propertiesByType: (state) => {
			return (type: PropertyType) => state.properties.filter((property) => property.type === type);
		},
		propertyByRef: (state) => {
			return (ref: DocumentReference): Property | undefined =>
				state.properties.find((property) => property.id === ref.id);
		},
		getOptions: (state): Property[] => {
			// First, sort the origins array alphabetically by the name property
			const sortedItems = [...state.properties].sort((a, b) => {
				return a.name.localeCompare(b.name);
			});

			// Then, map each sorted origin to a property option
			return sortedItems;
		},
		knacks: (state): Property[] => {
			return state.properties.filter((property) => property.type === PropertyType.knack);
		},
		quirks: (state): Property[] => {
			return state.properties.filter((property) => property.type === PropertyType.quirk);
		},
		drProperties: (state): Property[] => {
			return state.properties.filter((property) => 
				property.type === PropertyType.armor && 
				property.tag.startsWith('DR-')
			);
		}
	},

	actions: {
		async fetchAll(loadEffects: boolean = false) {
			// Fast path #1: properties already cached and caller doesn't
			// need effects resolved — return as-is.
			if (this.properties.length > 0 && !loadEffects) {
				return this.properties;
			}
			// Fast path #2: properties already cached (usually from a
			// bundle hydrate) AND effects are available in the effect
			// store — just run loadEffects over existing items. Saves
			// the /properties Firestore read on every bundle build.
			if (this.properties.length > 0 && loadEffects) {
				const effectStore = useEffectStore();
				if (effectStore.loaded) {
					console.time('⏱️ PropertyStore.fetchAll - cache-only loadEffects');
					for (const property of this.properties) {
						if (property) await this.loadEffects(property);
					}
					console.timeEnd('⏱️ PropertyStore.fetchAll - cache-only loadEffects');
					return this.properties;
				}
			}
			console.time('⏱️ PropertyStore.fetchAll');
			console.log('Fetching all properties', { loadEffects });
				const { $firestore } = useNuxtApp();
				console.time('⏱️ PropertyStore.fetchAll - getDocs');
				const querySnapshot = await getDocs(collection($firestore as Firestore, `/properties`));
				console.timeEnd('⏱️ PropertyStore.fetchAll - getDocs');

				console.time('⏱️ PropertyStore.fetchAll - mapping');
				let items = querySnapshot.docs.map((doc) => {
					return { ...getDefaultProperty(), id: doc.id, ref: doc.ref, ...doc.data() } as Property;
				});
				console.timeEnd('⏱️ PropertyStore.fetchAll - mapping');

				// Optionally load effects for all properties (default: skip for faster load)
				if (loadEffects) {
					console.time(`⏱️ PropertyStore.fetchAll - loadEffects (${items.length} items)`);
					for (const property of items) {
						if (property) {
							await this.loadEffects(property);
						}
					}
					console.timeEnd(`⏱️ PropertyStore.fetchAll - loadEffects (${items.length} items)`);
				} else {
					console.log(`⏱️ PropertyStore.fetchAll - SKIPPED loadEffects (${items.length} items) - will load lazily when needed`);
				}

				// Then update store - this batches the reactive updates
				console.time('⏱️ PropertyStore.fetchAll - updateStore');
				this.properties = items;
				console.timeEnd('⏱️ PropertyStore.fetchAll - updateStore');

				console.timeEnd('⏱️ PropertyStore.fetchAll');
				return items;
			// }
		},
		async fetchByType(propertyType: PropertyType, loadEffects: boolean = false) {
			console.time(`⏱️ PropertyStore.fetchByType(${propertyType})`);
			console.log('Fetching all properties', { propertyType, loadEffects });
			// const userStore = useUserStore();
			// if (userStore.isLoggedIn) {
				console.log('Length was 0 Fetching all properties');
				const { $firestore } = useNuxtApp();
				console.time(`⏱️ PropertyStore.fetchByType(${propertyType}) - getDocs`);
				const q = query(collection($firestore as Firestore, `/properties`), where("type", "==", propertyType));
				const querySnapshot = await getDocs(q);
				console.timeEnd(`⏱️ PropertyStore.fetchByType(${propertyType}) - getDocs`);

				console.time(`⏱️ PropertyStore.fetchByType(${propertyType}) - mapping`);
				let items = querySnapshot.docs.map((doc) => {
					return this.getDocFromSnapshot(doc);
				});
				console.timeEnd(`⏱️ PropertyStore.fetchByType(${propertyType}) - mapping`);

				// Optionally load effects for all properties (default: skip for faster load)
				if (loadEffects) {
					console.time(`⏱️ PropertyStore.fetchByType(${propertyType}) - loadEffects (${items.length} items)`);
					for (const property of items) {
						if (property) {
							await this.loadEffects(property);
						}
					}
					console.timeEnd(`⏱️ PropertyStore.fetchByType(${propertyType}) - loadEffects (${items.length} items)`);
				} else {
					console.log(`⏱️ PropertyStore.fetchByType(${propertyType}) - SKIPPED loadEffects (${items.length} items) - will load lazily when needed`);
				}

				// Then update store - this batches the reactive updates
				console.time(`⏱️ PropertyStore.fetchByType(${propertyType}) - updateStore`);
				for (const property of items) {
					if (property) {
						this.updateProperty(property);
					}
				}
				console.timeEnd(`⏱️ PropertyStore.fetchByType(${propertyType}) - updateStore`);

				console.timeEnd(`⏱️ PropertyStore.fetchByType(${propertyType})`);
				return items;
			// }
		},
		async fetchPropertiesByIds(propertyIds: string[], loadEffects: boolean = false): Promise<Property[]> {
			// First, try to get the properties from the cache
			const cachedProperties = propertyIds
				.map((id) => this.propertyById(id))
				.filter((prop): prop is Property => prop !== undefined);

			// If all requested properties are cached, return them directly
			if (cachedProperties.length === propertyIds.length) {
				return cachedProperties;
			}

			// Identify the missing properties
			const { $firestore } = useNuxtApp();
			const missingPropertyIds = propertyIds.filter((id) => !this.propertyById(id));

			// Fetch only the missing properties
			const fetchedPropertiesPromises = missingPropertyIds.map(async (id) => {
				const propertyRef = doc($firestore as Firestore, `/properties/${id}`);
				const snapshot = await getDoc(propertyRef);
				return snapshot.exists()
					? this.getDocFromSnapshot(snapshot)
					: null;
			});

			const fetchedProperties = await Promise.all(fetchedPropertiesPromises);

			// Optionally load effects for all fetched properties (default: skip for faster load)
			if (loadEffects) {
				for (const property of fetchedProperties) {
					if (property) {
						await this.loadEffects(property);
						cachedProperties.push(property);
					}
				}
			} else {
				// Just add to cache without loading effects
				for (const property of fetchedProperties) {
					if (property) {
						cachedProperties.push(property);
					}
				}
			}

			// Then update store - this batches the reactive updates
			for (const property of fetchedProperties) {
				if (property) {
					this.updateProperty(property);
				}
			}

			return cachedProperties;
		},
		async loadEffects(property: Property) {
			if (property.effects.length > 0) return property.effects;

			const effectStore = useEffectStore();

			// Bundle path: resolve from string IDs
			if (property._effectRefIds?.length) {
				property.effects = property._effectRefIds
					.map(id => effectStore.effectById(id))
					.filter((e): e is Effect => e !== undefined);
				return property.effects;
			}

			// Firestore path: resolve from DocumentReferences
			if (property.effectRefs && property.effectRefs.length > 0) {
				const effects = await effectStore.fetchEffectByRef(property.effectRefs);
				if (effects.length < property.effectRefs.length) {
					const loadedIds = new Set(effects.map(e => e.id));
					const orphaned = property.effectRefs.filter(ref => !loadedIds.has(ref.id)).map(ref => ref.id);
					console.warn(`⚠️ Property "${property.name}" (${property.id}) has ${orphaned.length} orphaned effectRefs: ${orphaned.join(', ')}`);
				}
				property.effects = effects;
				return effects;
			}

			return [];
		},
		async fetchPropertiesByRef(propertyRefs: DocumentReference[], loadEffects: boolean = false): Promise<Property[]> {
			const { $firestore } = useNuxtApp();
			// First, try to get the properties from the cache
			console.log(`propertyRefs length: ${propertyRefs.length} `);

			// Filter out any undefined/null refs and log malformed ones
			propertyRefs = propertyRefs.filter((ref, index) => {
				if (!ref || !ref.id) {
					console.warn(`⚠️ Skipping undefined/null propertyRef at index ${index}`);
					return false;
				}
				if (ref.id.includes(' ')) {
					console.error(`❌ MALFORMED REF #${index}: path="${ref.path}", id="${ref.id}" (contains space)`);
				}
				return true;
			});

			const cachedProperties = propertyRefs
				.map((ref: DocumentReference) => this.propertyByRef(ref))
				.filter((prop): prop is Property => prop !== undefined);
			console.log(`cachedProperties length: ${cachedProperties.length} `);

			// If all requested properties are cached, return them directly
			if (cachedProperties.length === propertyRefs.length) {
				return cachedProperties;
			}

			// Identify the missing property references, excluding failed fetches
			const missingPropertyRefs = propertyRefs.filter((ref) => {
				// Skip if already in cache
				if (this.propertyByRef(ref)) {
					return false;
				}
				// Skip if previously failed (prevents infinite loop)
				if (this.failedFetches.has(ref.path)) {
					console.warn(`⚠️ Skipping previously failed fetch for: ${ref.path}`);
					return false;
				}
				return true;
			});
			console.log(`missingPropertyRefs length: ${missingPropertyRefs.length} `);

			// Fetch only the missing properties
			const fetchedPropertiesPromises = missingPropertyRefs.map(async (ref: DocumentReference) => {
				// const propertyRef = doc($firestore as Firestore, ref);
				console.log(`Fetching property by ref: ${ref.path} ${ref.id}`);
				const snapshot = await getDoc(ref);

				if (snapshot.exists()) {
					return this.getDocFromSnapshot(snapshot);
				} else {
					// Mark this ref as failed to prevent future fetch attempts
					this.failedFetches.add(ref.path);
					console.warn(`❌ Property not found at path: ${ref.path} (added to failedFetches)`);
					return null;
				}
			});
			const fetchedProperties = await Promise.all(fetchedPropertiesPromises);

			// Optionally load effects for all fetched properties (default: skip for faster load)
			if (loadEffects) {
				for (const property of fetchedProperties) {
					if (property) {
						await this.loadEffects(property);
						cachedProperties.push(property);
					}
				}
			} else {
				// Just add to cache without loading effects
				for (const property of fetchedProperties) {
					if (property) {
						cachedProperties.push(property);
					}
				}
			}

			// Then update store - this batches the reactive updates
			for (const property of fetchedProperties) {
				if (property) {
					this.updateProperty(property);
				}
			}

			return cachedProperties;
		},
		getDocFromSnapshot(doc:  DocumentSnapshot<DocumentData, DocumentData> |   QueryDocumentSnapshot<DocumentData, DocumentData>) {
			return ({ ...getDefaultProperty(), id: doc.id, ref: doc.ref, ...doc.data() } as Property);
		},
		addProperty(newProperty: Property) {
			if (!this.propertyById(newProperty.id)) {
				this.properties.push(newProperty);
			}
		},
		async upsertProperty(dat: Property) : Promise<Property> {
			const userStore = useUserStore();
			if (userStore.isLoggedIn) {
				const { $firestore } = useNuxtApp();
				const propertiesRef = collection($firestore as Firestore, `/properties`);
				let propertyRef;

				// Check if the property already has an ID
				if (dat.id === '-1' || dat.id === undefined || !dat.id) {
					// Generate standardized ID: <type>-<name>
					dat.id = generatePropertyId(dat.type, dat.name);
					console.log(`Generated new property ID: ${dat.id}`);
					propertyRef = doc(propertiesRef, dat.id);
				} else {
					// Use existing ID
					propertyRef = doc(propertiesRef, dat.id);
				}

				// Set the ref on the property object (for local state)
				dat.ref = propertyRef;

				// Stamp ownership on first save. Never overwrite an existing
				// owner (e.g. system-owned public content edited by an admin).
				if (!dat.ownerId) {
					dat.ownerId = userStore.id || undefined;
				}
				// Public game data belongs to the shared system account so it
				// survives its author deleting their profile.
				if (dat.visibility === PropertyVisibility.Public) {
					dat.ownerId = SYSTEM_OWNER_UID;
				}

				// Prepare the updated property, stripping effects and ref (Firestore doesn't store these)
				const { effects, ref, ...updatedPropertyForFirestore } = dat;

				// Ensure internal ID matches document ID
				updatedPropertyForFirestore.id = dat.id;

				// Save or update the property in Firestore
				try {
					await setDoc(propertyRef, updatedPropertyForFirestore);
					this.updateProperty(dat);
					await this.selectProperty(dat.id);
				} catch (error) {
					console.error('Error saving Property: ', error);
				}
			}
			return dat;
		},
		updateProperty(dat: Property) {
			const index = this.properties.findIndex((property) => property.id === dat.id);

			if (index !== -1) {
				// Update existing property
				this.properties[index] = dat;
			} else {
				// Add new property
				this.properties.push(dat);
			}
		},
		removeProperty(propertyId: string) {
			this.properties = this.properties.filter((property) => property.id !== propertyId);
		},
		async selectProperty(propertyId: string) {
			console.log(`Selecting properties ${propertyId}`);
			const property = this.properties.find((property) => property.id === propertyId);
			if (property) {
				this.selectedProperty = property;
				// Automatically load effects when selecting a property
				await this.loadEffects(property);
			} else {
				console.log(`Properties ${propertyId} NOT FOUND`);
			}
		},
		clearSelectedProperty() {
			this.selectedProperty = undefined;
		},
		async removeEffect(property: Property, effect: Effect) {
			const userStore = useUserStore();
			if (userStore.isLoggedIn && property.id && effect.id) {
				const { $firestore } = useNuxtApp();
				const propertyRef = doc($firestore as Firestore, `/properties/${property.id}`);
		
				// Check if the effect exists in the property
				if (!property.effectRefs.some(ref => ref.id === effect.id)) {
					console.log('Effect does not exist in the property');
					return; // Optionally handle this case differently
				}
		
				const updatedEffectRefs = property.effectRefs.filter(ref => ref.id !== effect.id);
				const { effects, ...updatedPropertyForFirestore } = property;
				updatedPropertyForFirestore.effectRefs = updatedEffectRefs;
		
				// Save the updated property to Firestore
				try {
					await setDoc(propertyRef, updatedPropertyForFirestore);
		
					// Update the local state only if Firestore update is successful
					property.effectRefs = updatedEffectRefs;
					property.effects = property.effects.filter(eff => eff.id !== effect.id);

					// Update the property in the store
					this.updateProperty(property);
		
					// If this is the selected property, update the selectedProperty as well
					if (this.selectedProperty && this.selectedProperty.id === property.id) {
						this.selectedProperty = property;
					}
				} catch (error) {
					console.error('Error removing effect from property: ', error);
				}
			}
		},		
		async addEffect(property: Property, effect: Effect) {
			const userStore = useUserStore();
			if (userStore.isLoggedIn && property.id && effect.id) {
				const { $firestore } = useNuxtApp();
				const propertyRef = doc($firestore as Firestore, `/properties/${property.id}`);
				const effectRef = doc($firestore as Firestore, `/effects/${effect.id}`);

				// Check if the effect already exists in the property
				if (property.effectRefs.some(ref => ref.id === effect.id)) {
					console.log('Effect already exists in the property');
					return; // Optionally handle this case differently
				}

				try {
					// First, create/update the effect document in the effects collection
					await setDoc(effectRef, effect);
					console.log(`Effect created/updated in /effects/${effect.id}`);

					// Then add the effect reference to the property
					const updatedEffectRefs = [
						...property.effectRefs,
						effectRef,
					];

					// Prepare the updated property with the new effect
					const { effects, ...updatedPropertyForFirestore } = property;
					updatedPropertyForFirestore.effectRefs = updatedEffectRefs;

					// Save the updated property to Firestore
					await setDoc(propertyRef, updatedPropertyForFirestore);

					// Update the local state only if Firestore update is successful
					property.effectRefs = updatedEffectRefs;
					property.effects.push(effect);

					// Update the property in the store
					this.updateProperty(property);

					// If this is the selected property, update the selectedProperty as well
					if (this.selectedProperty && this.selectedProperty.id === property.id) {
						this.selectedProperty = property;
					}

					console.log(`Effect ${effect.id} added to property ${property.id}`);
				} catch (error) {
					console.error('Error adding effect to property: ', error);
				}
			}
		},

		async deleteProperty(property: Property) {
			const userStore = useUserStore();
			if (userStore.isLoggedIn && property.id) {
				const { $firestore } = useNuxtApp();
				const propertyRef = doc($firestore as Firestore, '/properties', property.id);
				await deleteDoc(propertyRef);
				this.properties = this.properties.filter((p) => p.id !== property.id);

				// Clear selected property if it was deleted
				if (this.selectedProperty?.id === property.id) {
					this.selectedProperty = undefined;
				}
			}
		},
	},
});
